/**
 * Ortto capture code (marketing automation / behavioural tracking).
 *
 * Loaded from App.tsx rather than index.html so that it can be gated on
 * marketing consent from the Cookie Information CMP - see getCookieDisclaimer.
 */

/**
 * Production reports to the "kilroyint" instance through Kilroy's own tracking
 * domain, which serves both app.js and the capture endpoint.
 */
const PROD_INSTANCE: IOrttoConfig = {
  captureKey: "ZV28fWwxCMok5-Rba2lscm95aW50",
  captureUrl: "https://newsletter.kilroy.net/",
  scriptUrl: "https://newsletter.kilroy.net/app.js",
};

/**
 * Everything else reports to the shared "kilroysandbox" instance, so QA and
 * local development never reach live marketing data. Same instance that
 * kint-websites-frontend uses for its non-production environments.
 *
 * Note this one is Ortto-hosted: app.js comes from their CDN rather than from
 * the capture domain, so the two URLs are unrelated and both hosts are
 * allowlisted in the CSP in etc/nginx.production.conf.
 */
const SANDBOX_INSTANCE: IOrttoConfig = {
  captureKey: "ZWmcTHGo5abrH3Z8a2lscm95c2FuZGJveA",
  captureUrl: "https://capture-api-eu.ortto.app/",
  scriptUrl: "https://cdneu.net/app.js",
};

const MARKETING_CONSENT_CATEGORY = "cookie_cat_marketing";
const CONSENT_EVENT = "CookieInformationConsentGiven";

export interface IOrttoConfig {
  captureKey: string;
  /** Passed to ap3c.init - where tracking data is sent. */
  captureUrl: string;
  /** Where the Ortto library itself is loaded from. */
  scriptUrl: string;
}

interface IAp3c {
  cmd: Array<() => void>;
  act?: unknown[];
  init: (captureKey: string, captureUrl: string) => void;
  track: (payload: { v: number }) => void;
  activity?: (act: unknown) => void;
}

interface IOrttoWindow extends Window {
  ap3c?: IAp3c;
  CookieInformation?: {
    getConsentGivenFor: (category: string) => boolean;
  };
}

/** Resolves which Ortto instance this environment reports to. */
export const getOrttoConfig = (environment?: string | null): IOrttoConfig =>
  environment?.toLowerCase() === "prod" ? PROD_INSTANCE : SANDBOX_INSTANCE;

const bootstrapOrtto = ({
  captureKey,
  captureUrl,
  scriptUrl,
}: IOrttoConfig) => {
  const target = window as IOrttoWindow;

  target.ap3c = target.ap3c || ({} as IAp3c);
  const ap3c = target.ap3c;
  ap3c.cmd = ap3c.cmd || [];
  ap3c.cmd.push(() => {
    ap3c.init(captureKey, captureUrl);
    ap3c.track({ v: 0 });
  });
  ap3c.activity = (act: unknown) => {
    ap3c.act = ap3c.act || [];
    ap3c.act.push(act);
  };

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.src = scriptUrl;
  document.head.appendChild(script);
};

/**
 * Waits for marketing consent, then loads Ortto. Nothing is requested and
 * window.ap3c is not defined until the visitor accepts marketing cookies.
 *
 * The CMP dispatches its event once per page load, so this has to be registered
 * before that script loads. Returns an unsubscribe function for effect cleanup;
 * note that app.js cannot be meaningfully unloaded once it has run, so
 * withdrawing consent only takes effect on the next page load.
 */
export const initOrttoOnConsent = (config: IOrttoConfig) => {
  const target = window as IOrttoWindow;

  const onConsentGiven = () => {
    if (target.ap3c) return;
    if (
      !target.CookieInformation?.getConsentGivenFor(MARKETING_CONSENT_CATEGORY)
    )
      return;

    bootstrapOrtto(config);
  };

  window.addEventListener(CONSENT_EVENT, onConsentGiven);
  return () => window.removeEventListener(CONSENT_EVENT, onConsentGiven);
};
